/** Campaign drafts written or corrected by hand. Ported from the old Drafts
 *  page (routes/Drafts.tsx): a hand-written post joins the same review queue
 *  as the generated ones, and the subreddit and title live in platform_meta
 *  because Reddit cannot take a post without a title. */

import { useEffect, useState } from 'react'
import type { Draft } from '@/api/client'
import { usePostActions } from '@/lib/postActions'
import { useCampaigns } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { Button, ErrorState, Field, FieldGroup, Input, MediaPicker, Select, THREADS_MAX_CHARS, Textarea, useToast } from '@/ui'

export function parseMeta(d: Pick<Draft, 'PlatformMeta'>): Record<string, unknown> {
  try {
    const v = JSON.parse(d.PlatformMeta || '{}')
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch {
    return {}
  }
}

export function parseMediaIds(d: Pick<Draft, 'MediaIDsJSON'>): number[] {
  try {
    const raw = JSON.parse(d.MediaIDsJSON || '[]')
    return Array.isArray(raw) ? raw.filter((v): v is number => typeof v === 'number') : []
  } catch {
    return []
  }
}

/** The judge scored the text as it was first written. Once a person has
 *  edited it, the score is about an older text, and says so. A hand-written
 *  draft was never judged (no attempts), so it has nothing to go stale. */
export function judgeIsStale(d: Pick<Draft, 'EditsCount' | 'JudgeAttempts'>): boolean {
  return (d.EditsCount ?? 0) > 0 && d.JudgeAttempts > 0
}

function ThreadsCounter({ text }: { text: string }) {
  const n = Array.from(text).length
  const over = n > THREADS_MAX_CHARS
  return (
    <span className={cn('tabular-nums', over ? 'font-medium text-status-failed' : 'text-muted-foreground')}>
      {n} / {THREADS_MAX_CHARS}
      {over && ' · 너무 길다'}
    </span>
  )
}

/** "캠페인 글 쓰기": queued for review, like a generated draft. */
export function DraftForm({ onDone }: { onDone: () => void }) {
  const campaigns = useCampaigns()
  const list = campaigns.data?.items ?? []
  const actions = usePostActions()
  const toast = useToast()
  const [campaignId, setCampaignId] = useState(0)
  const [platform, setPlatform] = useState('reddit')
  const [subreddit, setSubreddit] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [note, setNote] = useState('')
  const [err, setErr] = useState<unknown>(null)

  useEffect(() => {
    if (!campaignId && list.length > 0) setCampaignId(list[0].ID)
  }, [list.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const reddit = platform === 'reddit'
  const ready = campaignId > 0 && body.trim() !== '' && (!reddit || (subreddit.trim() !== '' && title.trim() !== ''))

  const submit = async () => {
    setErr(null)
    try {
      await actions.createDraft.mutateAsync({
        campaign_id: campaignId,
        platform,
        body_text: body,
        note,
        platform_meta: reddit ? { subreddit: subreddit.replace(/^r\//, ''), title } : {},
      })
      toast.success('검토 대기에 올렸다')
      onDone()
    } catch (e) {
      setErr(e)
    }
  }

  return (
    <div className="space-y-4">
      <FieldGroup columns={2}>
        <Field label="캠페인" required>
          <Select value={String(campaignId)} onChange={(e) => setCampaignId(Number(e.target.value))}>
            {list.length === 0 && <option value="0">— 캠페인이 아직 없다 —</option>}
            {list.map((c) => (
              <option key={c.ID} value={String(c.ID)}>
                {c.Name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="플랫폼" required>
          <Select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="reddit">Reddit</option>
            <option value="threads">Threads</option>
          </Select>
        </Field>
        {reddit && (
          <Field label="서브레딧" required>
            <Input value={subreddit} onChange={(e) => setSubreddit(e.target.value)} placeholder="AppGiveaway" />
          </Field>
        )}
        <Field label="메모" hint="글을 구분하려는 이름. 비워도 된다.">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="bori giveaway" />
        </Field>
        {reddit && (
          <Field label="제목" required hint="Reddit은 제목 없는 글을 받지 않는다." className="sm:col-span-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
      </FieldGroup>
      <Field label="본문" required aside={platform === 'threads' ? <ThreadsCounter text={body} /> : undefined}>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} className="font-mono text-table" />
      </Field>
      {err != null && <ErrorState title="올리지 못했다" error={err} />}
      <div className="flex justify-end gap-2">
        <Button onClick={onDone}>취소</Button>
        <Button variant="primary" loading={actions.createDraft.isPending} disabled={!ready} onClick={() => void submit()}>
          검토 대기에 올리기
        </Button>
      </div>
    </div>
  )
}

/** The edit tab of a campaign draft's detail: the body, Reddit's title and
 *  subreddit, the lead image. The draft's other meta keys are kept as they are
 *  — the old card sent an empty meta and lost the title and image on save. */
export function DraftEditor({ draft, onSaved, onCancel }: { draft: Draft; onSaved: () => void; onCancel: () => void }) {
  const actions = usePostActions()
  const toast = useToast()
  const meta = parseMeta(draft)
  const ids = parseMediaIds(draft)
  const reddit = draft.Platform === 'reddit'
  const [body, setBody] = useState(draft.BodyText)
  const [title, setTitle] = useState(String(meta.title ?? ''))
  const [subreddit, setSubreddit] = useState(String(meta.subreddit ?? ''))
  const [mediaId, setMediaId] = useState(ids[0] ?? 0)
  const [err, setErr] = useState<unknown>(null)
  const tooLong = draft.Platform === 'threads' && Array.from(body).length > THREADS_MAX_CHARS

  const save = async () => {
    setErr(null)
    const nextMeta = reddit ? { ...meta, title, subreddit: subreddit.replace(/^r\//, '') } : meta
    const nextIds = mediaId ? [mediaId, ...ids.slice(1).filter((x) => x !== mediaId)] : ids.slice(1)
    try {
      await actions.editDraft.mutateAsync({ id: draft.ID, body, mediaIds: nextIds, meta: nextMeta })
      toast.success('저장했다')
      onSaved()
    } catch (e) {
      setErr(e)
    }
  }

  return (
    <div className="space-y-4">
      <FieldGroup columns={2}>
        {reddit && (
          <>
            <Field label="제목" className="sm:col-span-2">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="서브레딧">
              <Input value={subreddit} onChange={(e) => setSubreddit(e.target.value)} />
            </Field>
          </>
        )}
        <Field label="이미지" hint={ids.length > 1 ? `첫 장만 바꾼다(모두 ${ids.length}장).` : undefined}>
          <MediaPicker value={mediaId} onChange={setMediaId} label="이미지" />
        </Field>
      </FieldGroup>
      <Field label="본문" aside={draft.Platform === 'threads' ? <ThreadsCounter text={body} /> : undefined}>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
      </Field>
      {err != null && <ErrorState title="저장하지 못했다" error={err} />}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>취소</Button>
        <Button variant="primary" loading={actions.editDraft.isPending} disabled={tooLong} onClick={() => void save()}>
          저장
        </Button>
      </div>
    </div>
  )
}
